import { useState } from "react";
import { Aviso, Boton, Etiqueta } from "../../components/ui/primitivos";
import {
  type AlertaDeOt,
  descargarDocumentoDeOt,
  fetchAlertaDeOt,
  revertirFacturacionOt,
} from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { accionesDeLaOt } from "./acciones";
import { DialogoRevertir } from "./DialogoRevertir";
import { ReabrirDesdeOt } from "./ReabrirDesdeOt";

/**
 * La facturación de una OT, en su detalle (ADR 0099 B3): estado de la alerta
 * vigente, número de factura, documentos y las acciones que corresponden.
 *
 * Quien la monta exige alguno de los permisos que acepta el endpoint de la
 * alerta por OT. Cada botón pide el suyo: descargar y revertir, `revertir`;
 * reabrir, `asignar` o `flexible_transition`.
 */

const ESTADO: Record<
  AlertaDeOt["estado"],
  { texto: string; tono: "ok" | "aviso" | "error" | "neutral" }
> = {
  pendiente: { texto: "Pendiente", tono: "error" },
  en_proceso: { texto: "En proceso", tono: "aviso" },
  facturado: { texto: "Facturado", tono: "ok" },
  no_facturable: { texto: "No se factura", tono: "neutral" },
  cancelado: { texto: "Cancelado", tono: "neutral" },
};

const DOCUMENTO: Record<AlertaDeOt["documentos"][number]["tipo"], string> = {
  comprobante: "Comprobante",
  nota_credito: "Nota de crédito",
};

async function descargar(otId: string, doc: AlertaDeOt["documentos"][number]) {
  const blob = await descargarDocumentoDeOt(otId, doc.tipo);
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = doc.nombreArchivo;
  enlace.click();
  URL.revokeObjectURL(url);
}

export function SeccionFacturacionOt({ otId }: { otId: string }) {
  const { can } = useSession();
  const alerta = useDatos(() => fetchAlertaDeOt(otId), otId);
  const [revirtiendo, setRevirtiendo] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState<string | null>(null);

  if (alerta.error) return <Aviso>{alerta.error}</Aviso>;
  if (alerta.cargando && alerta.datos === null) {
    return <p className="text-neutral-500 text-sm">Cargando facturación…</p>;
  }
  const a = alerta.datos;
  if (!a) {
    return <p className="text-neutral-500 text-sm">Sin alerta de facturación vigente.</p>;
  }

  const acciones = accionesDeLaOt(a.estado, {
    revertir: can("facturacion", "revertir"),
    // Reabrir: quien puede pasar una OT a CERRADA (ADR 0099 C4).
    decidir: can("produccion", "asignar") || can("produccion", "flexible_transition"),
  });
  const estado = ESTADO[a.estado];
  const puedeDescargar = can("facturacion", "revertir");

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-neutral-500 text-sm">Estado</span>
        <Etiqueta tono={estado.tono}>{estado.texto}</Etiqueta>
      </div>
      {a.facturaRef ? (
        <div className="flex items-center justify-between">
          <span className="text-neutral-500 text-sm">Factura</span>
          <span className="font-medium font-mono text-sm">{a.facturaRef}</span>
        </div>
      ) : null}
      {a.estado === "no_facturable" && a.motivoNoFacturable ? (
        <p className="text-neutral-600 text-sm">{a.motivoNoFacturable}</p>
      ) : null}

      {a.documentos.length > 0 ? (
        <ul className="space-y-1 border-neutral-200 border-t pt-3">
          {a.documentos.map((doc) => (
            <li key={doc.id} className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate text-sm" title={doc.nombreArchivo}>
                <span className="text-neutral-500">{DOCUMENTO[doc.tipo]}:</span> {doc.nombreArchivo}
              </span>
              {puedeDescargar ? (
                <Boton
                  variante="fantasma"
                  aria-label={`Descargar ${DOCUMENTO[doc.tipo].toLowerCase()}`}
                  onClick={async () => {
                    setErrorDescarga(null);
                    try {
                      await descargar(otId, doc);
                    } catch (err) {
                      setErrorDescarga(err instanceof Error ? err.message : "No se pudo descargar");
                    }
                  }}
                >
                  Descargar
                </Boton>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {errorDescarga ? <Aviso>{errorDescarga}</Aviso> : null}

      {acciones.includes("revertir") ? (
        <Boton variante="peligro" onClick={() => setRevirtiendo(true)}>
          Revertir facturación
        </Boton>
      ) : null}
      {acciones.includes("reabrir") ? (
        <ReabrirDesdeOt otId={otId} numero={a.numero} alCambiar={alerta.recargar} />
      ) : null}

      <DialogoRevertir
        abierto={revirtiendo}
        numero={a.numero}
        facturaRef={a.facturaRef}
        onCerrar={() => setRevirtiendo(false)}
        onConfirmar={async (datos) => {
          await revertirFacturacionOt(otId, datos);
          await alerta.recargar();
        }}
      />
    </div>
  );
}
